/**
 * `ui_block` specs — engine-authored interactive components rendered in
 * chat responses. Shared between rm-portal and rm-portal-beta (and, in
 * phase 2, the atlas-mcp-app iframe bundle) — pure JSON validation, no
 * framework deps. Mirror of the backend contract in
 * rm-api/src/agent-tools/workflow-types.ts; design doc:
 * docs/atlas-workflows-design.md.
 *
 * Unlike chart specs these are NEVER model-authored: the workflow engine
 * emits them inside tool results and the agent loop lifts them onto
 * `ChatResponse.ui_blocks` / persists them on assistant history messages.
 * Validation here is a render-time guard, not a trust boundary.
 */

/** Closed component vocabulary the portal renderer ships. */
export const UI_BLOCK_TYPES = ['choice', 'job_search', 'form', 'doc_list', 'doc_view', 'doc_upload'];

/**
 * Marker prefix for user messages produced by acting on a ui_block.
 * The model's tool guidance tells it to parse this line and call
 * `advance_workflow` with the embedded values; the portal renders the
 * marker line hidden so the user only sees the friendly part.
 */
export const WORKFLOW_ACTION_MARKER = '[workflow:action]';

/**
 * Marker for auto-start messages: sent by the host when a NEW
 * conversation begins in a context (page) configured with an
 * `atlas_context_workflow` route-meta override. The model's tool
 * guidance tells it to call `start_workflow` with the embedded key
 * immediately.
 */
export const WORKFLOW_START_MARKER = '[workflow:start]';

/**
 * Display copy for the engine's name-match confidence bands — the
 * rules in JobSearchService.matchTier, in users' words. `reason`
 * completes the sentence "this title <reason> <subject>", so one set of
 * copy serves every surface the pill appears on.
 */
export const MATCH_TIER_INFO = {
    high: { label: 'High match', reason: 'is the same as, or starts with,' },
    medium: { label: 'Medium match', reason: 'contains every word of' },
    low: { label: 'Low match', reason: 'only shares some words with' },
};

/** Pill text for a band ('' for anything that isn't one). */
export function matchTierLabel(tier) {
    return MATCH_TIER_INFO[tier] ? MATCH_TIER_INFO[tier].label : '';
}

/**
 * Document-status dot colours (DS prototype STATUS_COLOR): keyed by
 * normalized (lowercased, trimmed) status text; unknown statuses get
 * the neutral grey so the dot is never missing.
 */
const DOC_STATUS_COLOR = {
    draft: '#9CA3AF',
    'in progress': '#F59E0B',
    'in review': '#3B82F6',
    review: '#3B82F6',
    approved: '#16A34A',
    complete: '#16A34A',
    completed: '#16A34A',
    published: '#005073',
};

/** Dot colour for a document status ('' → neutral grey). */
export function docStatusColor(status) {
    return DOC_STATUS_COLOR[String(status ?? '').trim().toLowerCase()] || '#9CA3AF';
}

/**
 * Hover text: WHY a row is banded the way it is. `subject` names what
 * the title was compared against — the default suits cards where the
 * user typed it (search boxes, create forms); the upload flow passes
 * the uploaded document's name instead.
 */
export function matchTierTitle(tier, subject = 'the name you entered') {
    const info = MATCH_TIER_INFO[tier];
    return info ? `${info.label} — this title ${info.reason} ${subject}.` : '';
}

/**
 * Group doc rows for display: whenever any doc carries an
 * engine-emitted `family`, return interleaved header/doc rows grouped
 * by family (a single family still gets its heading — the name +
 * descriptor orient the user) — families ordered by first appearance,
 * so match-sorted input keeps the best-matching family first;
 * family-less docs group under "Other". Lists with no family data at
 * all stay flat (no grouping rule configured).
 */
export function groupDocRowsByFamily(docs) {
    const list = Array.isArray(docs) ? docs : [];
    // Group whenever family data is present — a single family still
    // gets its heading (name + descriptor orient the user); only lists
    // with NO family data at all render flat (no grouping rule
    // configured for the client).
    const families = new Set(list.map((d) => d && d.family).filter(Boolean));
    if (families.size === 0) return list.map((doc) => ({ type: 'doc', doc }));
    const groups = new Map();
    for (const doc of list) {
        const family = (doc && doc.family) || 'Other';
        if (!groups.has(family)) groups.set(family, []);
        groups.get(family).push(doc);
    }
    const rows = [];
    for (const [family, groupDocs] of groups) {
        // The family node's descriptor rides the heading when authored.
        const described = groupDocs.find((d) => d && d.family_description);
        rows.push({
            type: 'header',
            family,
            description: described ? described.family_description : null,
        });
        for (const doc of groupDocs) rows.push({ type: 'doc', doc });
    }
    return rows;
}

/**
 * Compose the auto-start message: marker line (machine-facing) +
 * friendly line (kept for the model; start-marker messages render no
 * user bubble). `opts` extends the marker for the Create-dialog
 * hand-off — `entryStepKey` starts the workflow parked on that step,
 * `initialValues` (a label→value map) pre-fills its form. Both ride
 * the marker LINE (entry=… value_json=…) because the start tool reads
 * them from the raw message verbatim — never via model-relayed params.
 */
export function buildWorkflowStartMessage(workflowKey, friendlyText, opts = {}) {
    if (!workflowKey) return null;
    let marker = `${WORKFLOW_START_MARKER} key=${workflowKey}`;
    if (opts.entryStepKey) marker += ` entry=${opts.entryStepKey}`;
    if (
        opts.initialValues &&
        typeof opts.initialValues === 'object' &&
        Object.keys(opts.initialValues).length > 0
    ) {
        marker += ` value_json=${JSON.stringify(opts.initialValues)}`;
    }
    return `${marker}\n${friendlyText || "Let's get started."}`;
}

/**
 * Normalize a tree field's nodes: drop nodes without a usable label,
 * mark nodes selectable only when they carry a non-empty value, and
 * prune branches that end up with nothing selectable beneath them.
 * Depth-capped as a cycle guard.
 */
function parseTreeNodes(nodes, depth = 0) {
    if (!Array.isArray(nodes) || depth > 10) return [];
    const out = [];
    for (const n of nodes) {
        if (!n || typeof n.label !== 'string' || !n.label.trim()) continue;
        const children = parseTreeNodes(n.children, depth + 1);
        const selectable =
            n.selectable === true && typeof n.value === 'string' && n.value.trim() !== '';
        if (!selectable && !children.length) continue;
        out.push({
            label: n.label,
            value: selectable ? n.value : undefined,
            node_id: Number.isFinite(n.node_id) ? n.node_id : undefined,
            hint: typeof n.hint === 'string' && n.hint.trim() ? n.hint : undefined,
            selectable,
            children: children.length ? children : undefined,
        });
    }
    return out;
}

/**
 * Parse and validate a ui_block. Returns a normalized block or null if
 * the shape is unusable — callers show a friendly error placeholder
 * instead of crashing (same contract as parseChartSpec).
 */
export function parseUiBlock(raw) {
    if (!raw || typeof raw !== 'object') return null;
    if (raw.version !== 1) return null;
    if (!UI_BLOCK_TYPES.includes(raw.type)) return null;
    if (typeof raw.block_id !== 'string' || raw.block_id.length === 0) return null;

    let workflow;
    if (raw.workflow && typeof raw.workflow === 'object') {
        const instanceId = Number(raw.workflow.instance_id);
        if (Number.isFinite(instanceId) && typeof raw.workflow.step_key === 'string') {
            workflow = {
                instance_id: instanceId,
                step_key: raw.workflow.step_key,
                status:
                    raw.workflow.status === 'completed' || raw.workflow.status === 'cancelled'
                        ? raw.workflow.status
                        : 'awaiting_input',
            };
            // Engine-stamped chip label for the parked-workflow resume
            // affordance (gate cards are generic `choice` blocks whose
            // type names nothing).
            if (
                typeof raw.workflow.resume_label === 'string' &&
                raw.workflow.resume_label.trim()
            ) {
                workflow.resume_label = raw.workflow.resume_label.trim();
            }
            // Exit-adjacent feedback score widget config (engine-stamped
            // from the workflow's config_json). Sanitized here so the
            // renderer can trust the shape.
            const fb = raw.workflow.feedback;
            if (
                fb &&
                typeof fb === 'object' &&
                typeof fb.label === 'string' &&
                fb.label.trim() &&
                Number.isFinite(Number(fb.count)) &&
                Number(fb.count) >= 2
            ) {
                workflow.feedback = {
                    label: fb.label,
                    count: Math.min(Math.trunc(Number(fb.count)), 10),
                    pre_label: typeof fb.pre_label === 'string' ? fb.pre_label : undefined,
                    post_label: typeof fb.post_label === 'string' ? fb.post_label : undefined,
                    text_trigger_max: Number.isFinite(Number(fb.text_trigger_max))
                        ? Math.trunc(Number(fb.text_trigger_max))
                        : undefined,
                    text_prompt:
                        typeof fb.text_prompt === 'string' && fb.text_prompt.trim()
                            ? fb.text_prompt
                            : undefined,
                    // Client-configured confirmation lines (engine-stamped
                    // from client settings; hosts fall back to their own
                    // defaults when absent).
                    saved_label:
                        typeof fb.saved_label === 'string' && fb.saved_label.trim()
                            ? fb.saved_label.trim()
                            : undefined,
                    comment_saved_label:
                        typeof fb.comment_saved_label === 'string' && fb.comment_saved_label.trim()
                            ? fb.comment_saved_label.trim()
                            : undefined,
                };
            }
            // "Create another" / "Upload another" (engine-stamped on the
            // card of a step that PRODUCED something). Sanitized like
            // feedback: the envelope is an ALLOW-LIST, so a field that
            // isn't copied here never reaches the renderer.
            const rp = raw.workflow.repeat;
            if (
                rp &&
                typeof rp === 'object' &&
                typeof rp.label === 'string' &&
                rp.label.trim() &&
                typeof rp.step_key === 'string' &&
                rp.step_key.trim()
            ) {
                workflow.repeat = { label: rp.label.trim(), step_key: rp.step_key.trim() };
            }
        }
    }

    if (raw.type === 'job_search') {
        const props = raw.props;
        if (!props || typeof props.prompt !== 'string' || !props.prompt.trim()) return null;
        return {
            version: 1,
            type: 'job_search',
            block_id: raw.block_id,
            workflow,
            props: {
                prompt: props.prompt,
                placeholder:
                    typeof props.placeholder === 'string' ? props.placeholder : undefined,
                // Restricts the card's searches to these doc types
                // (empty/omitted = client defaults).
                document_types: Array.isArray(props.document_types)
                    ? props.document_types.filter((t) => typeof t === 'string' && t.trim())
                    : undefined,
                // Extra filter inputs (engine-resolved labels + value
                // sources) — same degrade rules as form fields: a select
                // without options / a tree without nodes falls back to text.
                search_fields: Array.isArray(props.search_fields)
                    ? props.search_fields
                          .filter((f) => f && typeof f.attr === 'string' && f.attr.trim())
                          .map((f) => {
                              const options = Array.isArray(f.options)
                                  ? f.options.filter((o) => typeof o === 'string' && o.trim())
                                  : [];
                              const tree = f.type === 'tree' ? parseTreeNodes(f.tree) : [];
                              const type =
                                  f.type === 'tree' && tree.length
                                      ? 'tree'
                                      : f.type === 'select' && options.length
                                        ? 'select'
                                        : 'text';
                              return {
                                  attr: f.attr,
                                  label: typeof f.label === 'string' && f.label.trim() ? f.label : f.attr,
                                  placeholder:
                                      typeof f.placeholder === 'string' ? f.placeholder : undefined,
                                  required: f.required === true,
                                  type,
                                  // Engine-capped presentation hint: a small
                                  // select renders as toggleable pills.
                                  variant:
                                      type === 'select' && f.variant === 'pills' ? 'pills' : undefined,
                                  options: type === 'select' ? options : undefined,
                                  tree: type === 'tree' ? tree : undefined,
                                  // Tree filters only: the picker's filter box
                                  // doubles as free entry, so a typed value can
                                  // be searched on even with no matching node.
                                  free_text:
                                      type === 'tree' && f.free_text === true ? true : undefined,
                              };
                          })
                    : undefined,
                // Only honoured when there are field inputs to search with.
                hide_keyword:
                    props.hide_keyword === true &&
                    Array.isArray(props.search_fields) &&
                    props.search_fields.length > 0
                        ? true
                        : undefined,
                // next_step: the card collects criteria and advances with
                // trigger `searched`; a following doc_list shows results.
                results: props.results === 'next_step' ? 'next_step' : 'inline',
                // ENGINE-EMITTED (never authored): the previously recorded
                // criteria, present when the user came BACK to this search
                // — the card seeds its inputs so nothing is retyped.
                initial_criteria:
                    props.initial_criteria && typeof props.initial_criteria === 'object'
                        ? {
                              query:
                                  typeof props.initial_criteria.query === 'string'
                                      ? props.initial_criteria.query
                                      : undefined,
                              field_filters: Array.isArray(props.initial_criteria.field_filters)
                                  ? props.initial_criteria.field_filters.filter(
                                        (f) =>
                                            f &&
                                            typeof f.attr === 'string' &&
                                            Array.isArray(f.values),
                                    )
                                  : undefined,
                          }
                        : undefined,
            },
        };
    }

    if (raw.type === 'doc_list') {
        const props = raw.props;
        if (!props || typeof props.prompt !== 'string' || !props.prompt.trim()) return null;
        // items are ENGINE-RESOLVED; an empty list is a valid state the
        // card explains, but a missing array means an unresolved block.
        if (!Array.isArray(props.items)) return null;
        const items = props.items
            .filter(
                (d) =>
                    d &&
                    typeof d.reference === 'string' &&
                    d.reference.trim() &&
                    typeof d.name === 'string',
            )
            .map((d) => ({
                reference: d.reference,
                doc_id: Number(d.doc_id),
                name: d.name,
                doc_type: typeof d.doc_type === 'string' ? d.doc_type : undefined,
                status: typeof d.status === 'string' ? d.status : undefined,
                // Engine-emitted keyword-match confidence band.
                match: ['high', 'medium', 'low'].includes(d.match) ? d.match : undefined,
                // Engine-emitted grounded "why it matches" lines, shown
                // behind the row's disclosure.
                match_reasons: Array.isArray(d.match_reasons)
                    ? d.match_reasons
                          .filter((v) => typeof v === 'string' && v.trim())
                          .map((v) => v.trim())
                    : undefined,
                // Engine-emitted job family — lists group by it whenever
                // present (single family included).
                family: typeof d.family === 'string' && d.family.trim() ? d.family : undefined,
                family_description:
                    typeof d.family_description === 'string' && d.family_description.trim()
                        ? d.family_description
                        : undefined,
                // Engine-emitted row context: bottom-level values of the
                // doc's OTHER hierarchy placements (e.g. ["M1", "Gulf
                // of Mexico"]), shown beside the name.
                other_hierarchies: Array.isArray(d.other_hierarchies)
                    ? d.other_hierarchies
                          .filter((v) => typeof v === 'string' && v.trim())
                          .map((v) => v.trim())
                    : undefined,
                portal_url: typeof d.portal_url === 'string' ? d.portal_url : undefined,
            }));
        // Engine-emitted single action button under the list (e.g. the
        // duplicate-create gate's "Create anyway"). Clicking composes a
        // workflow-action message with `action` + `value`.
        const parseAction = (raw) =>
            raw &&
            typeof raw.label === 'string' &&
            raw.label.trim() &&
            typeof raw.action === 'string' &&
            raw.action.trim()
                ? { label: raw.label, action: raw.action, value: raw.value }
                : undefined;
        const footerAction = parseAction(props.footer_action);
        // Engine-emitted subtle secondary (the duplicate gate's Discard).
        const secondaryAction = parseAction(props.secondary_action);
        // Engine-emitted placement picker (create gate, NEW title): the
        // family/group the new node should sit under. Same degrade rules
        // as form fields — tree needs nodes, select needs options, else
        // free text. The pick RIDES the footer action, so without one
        // there is nothing to submit it with and the picker is dropped.
        let placementField;
        const pf = props.placement_field;
        if (
            footerAction &&
            pf &&
            typeof pf === 'object' &&
            typeof pf.key === 'string' &&
            pf.key.trim() &&
            typeof pf.label === 'string' &&
            pf.label.trim()
        ) {
            const options = Array.isArray(pf.options)
                ? pf.options.filter((o) => typeof o === 'string' && o.trim())
                : [];
            const tree = pf.type === 'tree' ? parseTreeNodes(pf.tree) : [];
            const type =
                pf.type === 'tree' && tree.length
                    ? 'tree'
                    : pf.type === 'select' && options.length
                      ? 'select'
                      : 'text';
            placementField = {
                key: pf.key,
                label: pf.label,
                type,
                tree: type === 'tree' ? tree : undefined,
                options: type === 'select' ? options : undefined,
                placeholder: typeof pf.placeholder === 'string' ? pf.placeholder : undefined,
            };
        }
        return {
            version: 1,
            type: 'doc_list',
            block_id: raw.block_id,
            workflow,
            props: {
                prompt: props.prompt,
                items,
                open_label:
                    typeof props.open_label === 'string' && props.open_label.trim()
                        ? props.open_label
                        : undefined,
                footer_action: footerAction,
                secondary_action: secondaryAction,
                // Engine-emitted: offer the standard compare over the
                // listed documents (duplicate-create gate).
                compare: props.compare === true || undefined,
                placement_field: placementField,
                placement_notice:
                    placementField &&
                    typeof props.placement_notice === 'string' &&
                    props.placement_notice.trim()
                        ? props.placement_notice
                        : undefined,
            },
        };
    }

    if (raw.type === 'doc_view') {
        const props = raw.props;
        const doc = props?.document;
        if (!doc || typeof doc.reference !== 'string' || !doc.reference.trim()) return null;
        const parts = (Array.isArray(doc.parts) ? doc.parts : [])
            .filter((p) => p && typeof p.name === 'string' && p.name.trim())
            .map((p) => ({
                name: p.name,
                text: typeof p.text === 'string' && p.text.trim() ? p.text : undefined,
                items: Array.isArray(p.items)
                    ? p.items.filter((i) => typeof i === 'string' && i.trim())
                    : undefined,
            }));
        return {
            version: 1,
            type: 'doc_view',
            block_id: raw.block_id,
            workflow,
            props: {
                prompt: typeof props.prompt === 'string' ? props.prompt : undefined,
                // Header + actions only, no inline content (e.g. the
                // doc_upload post-creation card — the document opens
                // itself and offers Open/Summary).
                compact: props.compact === true || undefined,
                confirm_label:
                    typeof props.confirm_label === 'string' ? props.confirm_label : undefined,
                // Engine-emitted. What Atlas just made, in one sentence.
                // Carried through so that opening this document can greet
                // in its own Atlas panel — drop it here and the document
                // opens with the workflow thread instead.
                created_note:
                    typeof props.created_note === 'string' && props.created_note.trim()
                        ? props.created_note.trim()
                        : undefined,
                document: {
                    reference: doc.reference,
                    doc_id: Number(doc.doc_id),
                    name: typeof doc.name === 'string' ? doc.name : doc.reference,
                    title: typeof doc.title === 'string' ? doc.title : undefined,
                    status: typeof doc.status === 'string' ? doc.status : undefined,
                    doc_type: typeof doc.doc_type === 'string' ? doc.doc_type : undefined,
                    portal_url: typeof doc.portal_url === 'string' ? doc.portal_url : undefined,
                    parts,
                    truncated: doc.truncated === true || undefined,
                },
            },
        };
    }

    if (raw.type === 'doc_upload') {
        const props = raw.props;
        if (!props || typeof props.prompt !== 'string' || !props.prompt.trim()) return null;
        // Engine-emitted phases: the dropzone, the pre-create review
        // (draft + captured attributes + the original file), and the
        // post-confirm "creating" wait state (document creation is in
        // flight; the created-document card follows as a background
        // message).
        const phase =
            props.phase === 'review' || props.phase === 'creating' ? props.phase : 'upload';
        let review;
        if (phase === 'review') {
            const r = props.review;
            if (!r || typeof r !== 'object') return null;
            review = {
                parts: Array.isArray(r.parts)
                    ? r.parts
                          .filter((p) => p && typeof p.name === 'string' && p.name.trim())
                          .map((p) => ({
                              name: p.name,
                              // Editing identity: part_id keys content edits
                              // back to the template part; multi allows row
                              // add/move/delete; a row-capped section is
                              // read-only (editing it would delete rows).
                              part_id:
                                  Number.isFinite(Number(p.part_id)) && Number(p.part_id) > 0
                                      ? Number(p.part_id)
                                      : undefined,
                              multi: p.multi === true || undefined,
                              items_truncated: p.items_truncated === true || undefined,
                              text: typeof p.text === 'string' && p.text.trim() ? p.text : undefined,
                              items: Array.isArray(p.items)
                                  ? p.items.filter((i) => typeof i === 'string' && i.trim())
                                  : undefined,
                          }))
                    : [],
                attributes: Array.isArray(r.attributes)
                    ? r.attributes.filter(
                          (a) => a && typeof a.label === 'string' && typeof a.value === 'string',
                      )
                    : [],
                original:
                    r.original && typeof r.original.filename === 'string'
                        ? {
                              filename: r.original.filename,
                              kind: typeof r.original.kind === 'string' ? r.original.kind : 'file',
                          }
                        : undefined,
                source_text:
                    typeof r.source_text === 'string' && r.source_text.trim()
                        ? r.source_text
                        : undefined,
                source_text_truncated: r.source_text_truncated === true || undefined,
                truncated: r.truncated === true || undefined,
                duplicates: Array.isArray(r.duplicates)
                    ? r.duplicates.filter(
                          (d) =>
                              d &&
                              typeof d.reference === 'string' &&
                              typeof d.name === 'string',
                      )
                    : undefined,
            };
        }
        return {
            version: 1,
            type: 'doc_upload',
            block_id: raw.block_id,
            workflow,
            props: {
                prompt: props.prompt,
                accept: Array.isArray(props.accept)
                    ? props.accept.filter((a) => typeof a === 'string' && a.trim())
                    : undefined,
                confirm_label:
                    typeof props.confirm_label === 'string' ? props.confirm_label : undefined,
                phase,
                review,
                creating:
                    phase === 'creating' &&
                    props.creating &&
                    typeof props.creating.filename === 'string'
                        ? { filename: props.creating.filename }
                        : undefined,
            },
        };
    }

    if (raw.type === 'form') {
        const props = raw.props;
        if (!props || typeof props.prompt !== 'string' || !props.prompt.trim()) return null;
        if (!Array.isArray(props.fields)) return null;
        const fields = props.fields
            .filter(
                (f) =>
                    f &&
                    typeof f.key === 'string' &&
                    f.key.trim() &&
                    typeof f.label === 'string' &&
                    f.label.trim(),
            )
            .map((f) => {
                // Options-based controls (select single, radio single,
                // checkbox MULTI — submits an array) need a usable
                // options list, and tree fields (hierarchy pickers —
                // submit { value, node_id, path }) a usable tree; without
                // one they degrade to free text rather than rendering an
                // empty control.
                const options = Array.isArray(f.options)
                    ? f.options.filter((o) => typeof o === 'string' && o.trim())
                    : [];
                const tree = f.type === 'tree' ? parseTreeNodes(f.tree) : [];
                const type =
                    f.type === 'textarea'
                        ? 'textarea'
                        : f.type === 'tree' && tree.length
                          ? 'tree'
                          : ['select', 'radio', 'checkbox'].includes(f.type) && options.length
                            ? f.type
                            : 'text';
                const isOptionType = ['select', 'radio', 'checkbox'].includes(type);
                return {
                    key: f.key,
                    label: f.label,
                    type,
                    options: isOptionType ? options : undefined,
                    tree: type === 'tree' ? tree : undefined,
                    // Tree pickers only: the filter box doubles as free
                    // entry — a typed value submits { value } with no node.
                    free_text: type === 'tree' && f.free_text === true ? true : undefined,
                    variant:
                        (type === 'radio' || type === 'checkbox') && f.variant === 'pills'
                            ? 'pills'
                            : undefined,
                    required: f.required === true,
                    placeholder: typeof f.placeholder === 'string' ? f.placeholder : undefined,
                    // Engine-emitted pre-fill (e.g. the doc_upload
                    // hierarchy confirmation seeds pickers with the
                    // chain-identified placement). Shape-checked per
                    // control: tree pick object, checkbox array, else
                    // string.
                    initial:
                        type === 'tree'
                            ? (f.initial && typeof f.initial === 'object' && typeof f.initial.value === 'string'
                                ? f.initial
                                : undefined)
                            : type === 'checkbox'
                              ? (Array.isArray(f.initial)
                                  ? f.initial.filter((v) => typeof v === 'string')
                                  : undefined)
                              : typeof f.initial === 'string' && f.initial
                                ? f.initial
                                : undefined,
                };
            });
        // An authored form always resolves to concrete fields, but the
        // engine's decision cards (content gate, placement confirmation)
        // are legitimately FIELDS-LESS — prompt + notice/preview +
        // buttons. Only a form whose declared fields were ALL malformed
        // is dropped.
        if (props.fields.length && !fields.length) return null;
        // Engine-emitted action beside the submit button (the gates'
        // "Change the details") — same shape as doc_list actions.
        const parseFormAction = (a) =>
            a &&
            typeof a.label === 'string' &&
            a.label.trim() &&
            typeof a.action === 'string' &&
            a.action.trim()
                ? { label: a.label, action: a.action, value: a.value }
                : undefined;
        // Engine-emitted placement confirmation panel: the family's jobs
        // in levelling order with the new job's row marked. Needs rows to
        // mean anything — without them it is dropped, and the card still
        // renders as a plain decision form.
        let placementPreview;
        const pp = props.placement_preview;
        if (pp && typeof pp === 'object') {
            const familyPath = Array.isArray(pp.family_path)
                ? pp.family_path.filter((s) => typeof s === 'string' && s.trim())
                : [];
            const rows = Array.isArray(pp.rows)
                ? pp.rows
                      .filter((r) => r && typeof r.title === 'string' && r.title.trim())
                      .map((r) => ({
                          abbr: typeof r.abbr === 'string' && r.abbr.trim() ? r.abbr : undefined,
                          title: r.title,
                          is_new: r.is_new === true || undefined,
                      }))
                : [];
            if (rows.length) {
                placementPreview = {
                    family_path: familyPath,
                    rows,
                    note: typeof pp.note === 'string' && pp.note.trim() ? pp.note : undefined,
                };
            }
        }
        // Engine-emitted levelling cascade: which field keys play the
        // level / track / abbreviation roles, plus the framework's valid
        // combinations. The renderer narrows a role field's options to
        // the combos agreeing with values picked in EARLIER role fields.
        // Needs 2+ roles bound to real fields to mean anything.
        let levelling;
        const lv = props.levelling;
        if (lv && typeof lv === 'object' && lv.keys && typeof lv.keys === 'object' && Array.isArray(lv.combos)) {
            const fieldKeys = new Set(fields.map((f) => f.key));
            const keys = {};
            for (const role of ['level', 'track', 'abbreviation']) {
                const k = lv.keys[role];
                if (typeof k === 'string' && k.trim() && fieldKeys.has(k)) keys[role] = k;
            }
            const combos = lv.combos
                .filter((c) => c && typeof c === 'object' && typeof c.level === 'string' && c.level.trim())
                .map((c) => ({
                    level: c.level,
                    track: typeof c.track === 'string' && c.track.trim() ? c.track : undefined,
                    abbreviation:
                        typeof c.abbreviation === 'string' && c.abbreviation.trim()
                            ? c.abbreviation
                            : undefined,
                }));
            if (Object.keys(keys).length >= 2 && combos.length) levelling = { keys, combos };
        }
        return {
            version: 1,
            type: 'form',
            block_id: raw.block_id,
            workflow,
            props: {
                prompt: props.prompt,
                fields,
                submit_label:
                    typeof props.submit_label === 'string' ? props.submit_label : undefined,
                // Engine-emitted: why the form came back / what to see
                // before submitting again.
                notice:
                    typeof props.notice === 'string' && props.notice.trim()
                        ? props.notice
                        : undefined,
                secondary_action: parseFormAction(props.secondary_action),
                placement_preview: placementPreview,
                levelling,
            },
        };
    }

    if (raw.type === 'choice') {
        const props = raw.props;
        if (!props || typeof props.prompt !== 'string' || !props.prompt.trim()) return null;
        if (!Array.isArray(props.options)) return null;
        const options = props.options
            .filter(
                (o) =>
                    o &&
                    typeof o.key === 'string' &&
                    o.key.trim() &&
                    typeof o.label === 'string' &&
                    o.label.trim(),
            )
            .map((o) => ({
                key: o.key,
                label: o.label,
                description: typeof o.description === 'string' ? o.description : undefined,
                // Optional icon name for the option's leading tile (the
                // renderer falls back to a keyword heuristic when absent).
                icon:
                    typeof o.icon === 'string' && o.icon.trim()
                        ? o.icon.trim()
                        : undefined,
                // Presentation extras: a small pill next to the label and a
                // right-aligned meta hint (the generate-choice gate's
                // "Recommended" / "Lowest effort").
                badge: typeof o.badge === 'string' ? o.badge : undefined,
                hint: typeof o.hint === 'string' ? o.hint : undefined,
                // Engine-annotated permission gating — render disabled with
                // the reason; the engine also refuses the action server-side.
                disabled: o.disabled === true || undefined,
                disabled_reason:
                    typeof o.disabled_reason === 'string' ? o.disabled_reason : undefined,
            }));
        if (options.length < 2) return null;
        return {
            version: 1,
            type: 'choice',
            block_id: raw.block_id,
            workflow,
            props: { prompt: props.prompt, options },
        };
    }

    return null;
}

/**
 * Compose the user message sent when the user acts on a workflow-bound
 * block. Marker line first (the model's guidance keys off "starts
 * with"), friendly line second (what the user bubble displays after
 * `stripWorkflowActionMarker`).
 *
 * `option` is `{ key, label, value? }` — `value` carries the action's
 * payload for the model to pass through to `advance_workflow`:
 *  - string (job_search → the chosen RM reference; must be a single
 *    token, which RM references always are) → appended as `value=…`;
 *  - object (form → the submitted field map) → appended as
 *    `value_json={…}` (compact JSON on the marker line).
 * `option.label` is the friendly line shown in the user's bubble — for
 * forms, a multi-line summary of what they entered.
 */
export function buildWorkflowActionMessage(block, option) {
    const wf = block && block.workflow;
    if (!wf) return null;
    let valuePart = '';
    if (option.value !== undefined && option.value !== null) {
        if (typeof option.value === 'object') {
            valuePart = ` value_json=${JSON.stringify(option.value)}`;
        } else if (`${option.value}`.trim() !== '') {
            valuePart = ` value=${`${option.value}`.trim()}`;
        }
    }
    return (
        `${WORKFLOW_ACTION_MARKER} instance_id=${wf.instance_id} step_key=${wf.step_key} action=${option.key}${valuePart}\n` +
        `${option.label}`
    );
}

/**
 * Message the hosts' "Resume workflow" chip sends for a parked
 * instance: the resume marker (deterministically routed to
 * advance_workflow) plus the friendly bubble line. The engine ignores
 * the step_key for resume — its own pointer wins — but the marker
 * carries it for the audit trail.
 */
export function buildWorkflowResumeMessage(instanceId, stepKey, label) {
    if (instanceId === undefined || instanceId === null) return null;
    const friendly =
        typeof label === 'string' && label.trim() ? label.trim() : 'Resume workflow';
    return (
        `${WORKFLOW_ACTION_MARKER} instance_id=${instanceId} step_key=${stepKey} action=resume\n` +
        friendly
    );
}

/**
 * Display text for user bubbles: hide the machine-facing marker line,
 * keep the friendly remainder. Non-action messages pass through
 * untouched. Applied at render time (live and replayed messages take
 * the same path).
 */
export function stripWorkflowActionMarker(text) {
    if (
        typeof text !== 'string' ||
        !(text.startsWith(WORKFLOW_ACTION_MARKER) || text.startsWith(WORKFLOW_START_MARKER))
    ) {
        return text;
    }
    const newline = text.indexOf('\n');
    if (newline === -1) return text;
    return text.slice(newline + 1).trim() || text;
}
